import React, { useCallback, useMemo } from 'react';

// DEPENDENCIES
import { useFormikContext } from 'formik';
import trim from 'lodash/trim';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';

// GLOBAL FUNCTIONS
import { makePath } from 'functions';

// HELPERS
import useRecordView from 'helpers/getRecordView';
import { useRecordContext } from 'helpers/getRecordData';

// CORE COMPONENTS
import Form from 'core/form/Form';
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import DownloadRecent from './DownloadRecentButton';
import AttachmentDropdown from './AttachmentDropdown';

// STYLES
import styles from './attachmentUpload.module.scss';

// VARIABLES
export const WARNING_MESSAGE = (
  <div className="text-left text-danger large">
    <p>
      Warning: Please confirm that all documents you are about to upload DO NOT contain any customer
      sensitive information.
    </p>
    <p className="mb-4">
      Customer sensitive information such as the full Social Security number and or the full Credit / Debit
      Card information must be REDACTED before uploading.
    </p>
  </div>
)

// COMPONENTS
const UploadButton = ({ name, useDocUpr, useTempDocUpr, downloadRecent, ...props }) => (
  <div className="d-sm-flex">
    {useDocUpr || useTempDocUpr ?
      <AttachmentDropdown useTemp={useTempDocUpr} {...props} /> : <Button {...props} />
    }
    {downloadRecent &&
      <DownloadRecent name={name} />
    }
  </div>
);

// MAIN COMPONENT
const AttachmentUpload = ({
  documentType,
  documentCode,
  uploadParams,
  requestParams,
  requestedFor,
  downloadRecent,
  required,
  ...props
}) => {

  // FORMIK CONTEXT
  const { values } = useFormikContext();

  // HOOKS
  const { isAdd, view, isView } = useRecordView();
  const { record } = useRecordContext() || {};

  // MEMOS
  const {
    // PROPS
    name,
    docrName,
    uploadType = view,
    // RECORD/FORM VALUES
    daily_sales_inventory_id,
    sales_id,
    stock_num,
    vin_num,
    // DERIVED
    isSalesView = ['sales', 'amFinance', 'order', 'deliveryLog'].includes(view),
    isInventoryType = uploadType === 'inventory',
    isSalesType = uploadType === 'sales',
    useTemp = !isView,
    useDocUpr = !isAdd,
    useTempDocUpr = isAdd || !!props.docrName,
    noVinStock = isInventoryType && isAdd && (!stock_num || !vin_num),
    vehicle
  } = useMemo(
    () => Object.assign({}, props, record, record?.inventory, values, values?.inventory),
    [props, values, record]
  )
  const params = useMemo(
    () => {
      const params = {
        documentType,
        isSalesUpload: isSalesView ? 1 : 0
      };
      if (isInventoryType) {
        if (daily_sales_inventory_id) { // If Existing inventory item.
          params.dailySalesInventoryId = daily_sales_inventory_id;
        } else {
          params.stockNum = stock_num;
          params.vinNum = vin_num;
        }
      }
      if (isSalesType) {
        if (sales_id) { // If Existing sales item.
          params.salesId = sales_id;
        } else {
          params.dailySalesInventoryId = daily_sales_inventory_id;
        }
      }
      return Object.assign(params, uploadParams)
    },
    [daily_sales_inventory_id, sales_id, stock_num, vin_num, isInventoryType, isSalesType, isSalesView, documentType, uploadParams]
  )

  const disabled = noVinStock;
  
  const handleUploadMeta = useCallback(
    data => ({
      temp: !!data.temp_attachment_id,
      attachmentID: data.temp_attachment_id || data[`${uploadType}attachment_id`] || data.attachmentId,
      fileName: data.temp_attachment_filename || data[`${uploadType}attachment_filename`] || data.fileName || '',
      uploadedVia: trim(data.uploaded_via || data.uploadedVia),
      uploadedBy: trim(data.updated_by || data.created_by || data.uploadedBy),
      uploadedOn: trim(data.date_updated || data.date_created || data.timecreated),
      requestedBy: trim(data.requester_name),
      requestedOn: trim(data.request_date)
    }),
    [uploadType]
  )

  // RENDER
  return (
    <Form.Upload
      label="Upload"
      button={{
        as: UploadButton,
        variant: useDocUpr || useTempDocUpr ? 'custom' : undefined,
        className: styles.button,
        name,
        docrName,
        documentType,
        uploadType,
        requestParams,
        requestedFor,
        useDocUpr,
        useTempDocUpr,
        downloadRecent,
        vehicle
      }}
      uploadArgs={
        () => ({
          endpoint: useTemp ? ENDPOINTS.uploads[uploadType].temp : makePath(ENDPOINTS.uploads[uploadType].upload, sales_id),
          params
        })
      }
      viewArgs={
        (_uploadID, _file, meta) => ({
          endpoint: makePath(ENDPOINTS.uploads[meta.temp ? 'temp' : uploadType].view, meta.attachmentID)
        })
      }
      deleteArgs={
        (_uploadID, _file, meta) => ({
          method: 'DELETE',
          endpoint: makePath(ENDPOINTS.uploads[meta.temp ? 'temp' : uploadType].delete, meta.attachmentID),
          loadingMessage: 'Removing file',
          successMessage: 'File was removed successfully.'
        })
      }
      uploadMeta={handleUploadMeta}
      uploadFieldValue={
        ({ message, status, ...data }) => {
          return isSalesView ? {
            temp_attachment_id: data.temp_attachment_id,
            [`${uploadType}attachment_id`]: '',
            [`${uploadType}attachment_filename`]: data.fileName,
            [`${uploadType}attachment_type_code`]: documentCode,
            [`${uploadType}attachment_type_id`]: documentType,
            created_by_id: data.created_by_site_user_id,
            date_created: data.timecreated,
            date_updated: data.timecreated,
            uploadedVia: trim(data.uploaded_via || data.uploadedVia),
            uploadedBy: trim(data.updated_by || data.created_by || data.uploadedBy),
          } : data;
        }
      }
      multiple
      required={!disabled && required}
      modal={{
        message: WARNING_MESSAGE
      }}
      disabled={disabled}
      text={noVinStock && 'VIN and Stock # are required.'}
      {...props}
    />
  );
};

// EXPORT
export default AttachmentUpload;
